"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, useQuery } from "@opensea/graphql";
import { useEffect, useMemo, useState } from "react";
import { isAddress, isEnsName } from "../lib/address";
import { useAddress } from "../polychain/hooks";
import { useSyncFormFields } from "./useSyncFormField";

// Utility type to get keys from T where the value type matches V

const query = graphql(`
  query useAddressInputQuery($identifier: String!) {
    profileByIdentifier(identifier: $identifier) {
      __typename
      ... on Profile {
        address
        imageUrl
        ensName
      }
    }
  }
`);
const CONTEXT = {
  suspense: false
};
export const useAddressInput = t0 => {
  const $ = _c(23);
  const {
    identifier: t1
  } = t0;
  const identifier = t1 === undefined ? "" : t1;
  const currentAddress = useAddress();
  let t2;
  if ($[0] !== identifier) {
    t2 = {
      identifier
    };
    $[0] = identifier;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !(isEnsName(identifier) || isAddress(identifier));
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = {
      query,
      variables: t2,
      context: CONTEXT,
      pause: t3
    };
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [t5] = useQuery(t4);
  const {
    data,
    fetching
  } = t5;
  const [dataIdentifier, setDataIdentifier] = useState(identifier);
  let t6;
  if ($[5] !== identifier) {
    t6 = () => {
      setDataIdentifier(identifier);
    };
    $[5] = identifier;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== data) {
    t7 = [data];
    $[7] = data;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  bb0: {
    if (fetching) {
      t8 = undefined;
      break bb0;
    }
    if (!(isEnsName(identifier) || isAddress(identifier))) {
      t8 = undefined;
      break bb0;
    }
    if (!identifier) {
      t8 = undefined;
      break bb0;
    }
    if (data?.profileByIdentifier === null) {
      t8 = null;
      break bb0;
    }
    if (data?.profileByIdentifier?.__typename === "Profile") {
      t8 = data.profileByIdentifier.address;
      break bb0;
    }
    t8 = undefined;
  }
  const resolvedAddress = t8;
  let t9;
  bb1: {
    if (data?.profileByIdentifier?.__typename !== "Profile") {
      let t10;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = {};
        $[9] = t10;
      } else {
        t10 = $[9];
      }
      t9 = t10;
      break bb1;
    }
    if (dataIdentifier === identifier) {
      let t10;
      if ($[10] !== data.profileByIdentifier.ensName || $[11] !== identifier) {
        t10 = isEnsName(identifier) ? identifier : data.profileByIdentifier.ensName;
        $[10] = data.profileByIdentifier.ensName;
        $[11] = identifier;
        $[12] = t10;
      } else {
        t10 = $[12];
      }
      let t11;
      if ($[13] !== data.profileByIdentifier.imageUrl || $[14] !== t10) {
        t11 = {
          imageUrl: data.profileByIdentifier.imageUrl,
          ensName: t10
        };
        $[13] = data.profileByIdentifier.imageUrl;
        $[14] = t10;
        $[15] = t11;
      } else {
        t11 = $[15];
      }
      t9 = t11;
      break bb1;
    }
    let t10;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {};
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t9 = t10;
  }
  const {
    imageUrl,
    ensName
  } = t9;
  const t10 = resolvedAddress ?? undefined;
  const t11 = ensName ?? undefined;
  let t12;
  if ($[17] !== currentAddress || $[18] !== fetching || $[19] !== imageUrl || $[20] !== t10 || $[21] !== t11) {
    t12 = {
      currentAddress,
      isResolving: fetching,
      resolvedAddress: t10,
      imageUrl,
      ensName: t11
    };
    $[17] = currentAddress;
    $[18] = fetching;
    $[19] = imageUrl;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
};
export const useAddressInputField = t0 => {
  const $ = _c(24);
  const {
    field,
    identifier: t1,
    setValue
  } = t0;
  const identifier = t1 === undefined ? "" : t1;
  let t2;
  if ($[0] !== identifier) {
    t2 = {
      identifier
    };
    $[0] = identifier;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    currentAddress,
    isResolving,
    resolvedAddress,
    imageUrl,
    ensName
  } = useAddressInput(t2);
  const t3 = `${field}.currentAddress`;
  let t4;
  if ($[2] !== currentAddress || $[3] !== t3) {
    t4 = getNameAndValue({
      name: t3,
      value: currentAddress
    });
    $[2] = currentAddress;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const currentAddressField = t4;
  const t5 = `${field}.isResolving`;
  let t6;
  if ($[5] !== isResolving || $[6] !== t5) {
    t6 = getNameAndValue({
      name: t5,
      value: isResolving
    });
    $[5] = isResolving;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const isResolvingField = t6;
  const t7 = `${field}.resolvedAddress`;
  let t8;
  if ($[8] !== resolvedAddress || $[9] !== t7) {
    t8 = getNameAndValue({
      name: t7,
      value: resolvedAddress
    });
    $[8] = resolvedAddress;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const resolvedAddressField = t8;
  let t9;
  if ($[11] !== currentAddressField || $[12] !== isResolvingField || $[13] !== resolvedAddressField) {
    t9 = [currentAddressField, isResolvingField, resolvedAddressField];
    $[11] = currentAddressField;
    $[12] = isResolvingField;
    $[13] = resolvedAddressField;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = t9;
  let t11;
  if ($[15] !== setValue || $[16] !== t10) {
    t11 = {
      fields: t10,
      setValue
    };
    $[15] = setValue;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  useSyncFormFields(t11);
  const t12 = resolvedAddress ?? undefined;
  const t13 = ensName ?? undefined;
  let t14;
  if ($[18] !== currentAddress || $[19] !== imageUrl || $[20] !== isResolving || $[21] !== t12 || $[22] !== t13) {
    t14 = {
      currentAddress,
      isResolving,
      resolvedAddress: t12,
      imageUrl,
      ensName: t13
    };
    $[18] = currentAddress;
    $[19] = imageUrl;
    $[20] = isResolving;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  return t14;
};
const getNameAndValue = ({
  name,
  value
}) => {
  return {
    name,
    value
  };
};